import { useState } from 'react'

import { api } from '../api/client'
import type { CraCategory, CraProduct, CraProductIn, CraRelease, CraRole, Criticita } from '../api/types'
import { CRITICALITY_LABEL } from '../pages/Services'
import { CRITICITA, CriticitaBadge } from './BusinessApps'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { ErrorBanner } from './common'
import { CATEGORIE, daLocale, locale, RUOLI } from './Cra'
import { SelettoreFornitore } from './Fornitori'
import { Finestra } from './ModificheGitlab'

/**
 * CRA register: the dialogs — a product's card, a new report by hand, a
 * release by hand (also one already on the market, art. 69(3), with its SBOM).
 */

const VUOTO: CraProductIn = {
  name: '', description: '', in_scope: true, scope_reason: '', category: 'default', role: 'manufacturer',
  support_until: null, support_reason: '', vuln_contact: '', cvd_policy_url: '', website: '', owner_email: '',
  csirt: 'CSIRT Italia', member_states: ['IT'], manufacturer_name: '', manufacturer_contact: '', release_rule: 'tag',
  project_ids: [],
}

export function FinestraProdotto({ attuale, onChiudi, onFatto }: {
  attuale?: CraProduct
  onChiudi: () => void
  onFatto: (p: CraProduct) => void
}) {
  const [d, setD] = useState<CraProductIn>(attuale ? { ...VUOTO, ...attuale } : VUOTO)
  const [stati, setStati] = useState((attuale?.member_states ?? VUOTO.member_states).join(', '))
  const progetti = useAsync(() => api.supplyProjects().catch(() => []), [])
  const app = useAsync(() => api.businessApps().catch(() => []), [])
  const appScelta = (app.data ?? []).find((a) => a.id === d.business_app_id)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const importa = d.role === 'importer' || d.role === 'distributor'

  async function salva() {
    setErrore(null)
    setLavoro(true)
    const corpo = { ...d, member_states: stati.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean) }
    try {
      onFatto(attuale ? await api.craProductUpdate(attuale.id, corpo) : await api.craProductCreate(corpo))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const campo = (k: keyof CraProductIn, etichetta: string, aiuto?: string, tipo = 'text') => (
    <div className="field" style={{ flex: 1, minWidth: 240 }}>
      <label htmlFor={`cra-${k}`}>{etichetta}</label>
      <input id={`cra-${k}`} type={tipo} value={String(d[k] ?? '')} onChange={(e) => setD({ ...d, [k]: e.target.value })} />
      {aiuto && <div className="field__hint">{aiuto}</div>}
    </div>
  )

  return (
    <Finestra titolo={attuale ? t('Scheda del prodotto') : t('Nuovo prodotto')} sotto={attuale?.name} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        {campo('name', t('Nome'))}
        <div className="field" style={{ minWidth: 240 }}>
          <label htmlFor="cra-categoria">{t('Categoria di legge')}</label>
          <select id="cra-categoria" value={d.category} onChange={(e) => setD({ ...d, category: e.target.value as CraCategory })}>
            {CATEGORIE.map(([k, l]) => <option key={k} value={k}>{l()}</option>)}
          </select>
        </div>
      </div>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ flex: 1, minWidth: 240 }}>
          <label htmlFor="cra-app">{t('Business App')}</label>
          <select id="cra-app" value={d.business_app_id ?? ''} onChange={(e) => setD({ ...d, business_app_id: e.target.value || null })}>
            <option value="">{t('nessuna')}</option>
            {(app.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div className="field__hint">{t('Il prodotto è questa applicazione: la sua criticità per l’organizzazione è quella della Business App (la più alta delle sue funzionalità).')}</div>
        </div>
        <div className="field" style={{ minWidth: 220 }}>
          <label htmlFor="cra-criticita">{t('Criticità per l’organizzazione')}</label>
          <select id="cra-criticita" value={d.criticality_override ?? ''}
                  onChange={(e) => setD({ ...d, criticality_override: (e.target.value || null) as Criticita | null })}>
            <option value="">{appScelta?.criticality
              ? t('dalla Business App: {c}', { c: CRITICALITY_LABEL[appScelta.criticality] ?? appScelta.criticality })
              : t('da definire')}</option>
            {CRITICITA.map((c) => <option key={c} value={c}>{CRITICALITY_LABEL[c] ?? c}</option>)}
          </select>
          <div className="field__hint">{t('Non è la categoria di legge (allegati III e IV): dice quanto il prodotto conta, per ordinare le cose da valutare.')}</div>
        </div>
      </div>
      <div className="field">
        <label htmlFor="cra-descrizione">{t('Descrizione')}</label>
        <textarea id="cra-descrizione" rows={2} value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} />
      </div>
      <div className="checkbox">
        <input id="cra-ambito" type="checkbox" checked={d.in_scope} onChange={(e) => setD({ ...d, in_scope: e.target.checked })} />
        <label htmlFor="cra-ambito">{t('Nel campo del CRA')}<small>{t('Un prodotto con elementi digitali messo a disposizione sul mercato UE. Fuori: i servizi SaaS senza componenti installati dal cliente, il software usato solo internamente, i prodotti regolati da altre norme (dispositivi medici, veicoli…).')}</small></label>
      </div>
      {!d.in_scope && (
        <div className="field">
          <label htmlFor="cra-motivo">{t('Perché è fuori dal CRA')}</label>
          <textarea id="cra-motivo" rows={2} value={d.scope_reason} onChange={(e) => setD({ ...d, scope_reason: e.target.value })} />
        </div>
      )}
      <fieldset className="field">
        <legend>{t('Ruolo dell’organizzazione')}</legend>
        {RUOLI.map(([k, l, h]) => (
          <div className="checkbox" key={k}>
            <input id={`ruolo-${k}`} type="radio" name="ruolo" checked={d.role === k} onChange={() => setD({ ...d, role: k as CraRole })} />
            <label htmlFor={`ruolo-${k}`}>{l()}<small>{h()}</small></label>
          </div>
        ))}
      </fieldset>
      {importa && (
        <div className="toolbar" style={{ alignItems: 'flex-start' }}>
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="cra-fornitore">{t('Dal registro fornitori')}</label>
            <SelettoreFornitore value={d.supplier_id ?? null} onChange={(id) => setD({ ...d, supplier_id: id })} />
            <small className="text-muted">{t('Riempie nome e contatto se sono vuoti.')}</small>
          </div>
          {campo('manufacturer_name', t('Fabbricante da informare'))}
          {campo('manufacturer_contact', t('Contatto del fabbricante per le vulnerabilità'))}
        </div>
      )}
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor="cra-supporto">{t('Supporto fino al')}</label>
          <input id="cra-supporto" type="date" value={d.support_until ?? ''} onChange={(e) => setD({ ...d, support_until: e.target.value || null })} />
          <div className="field__hint">{t('Almeno cinque anni, salvo un uso previsto più breve (art. 13(8)).')}</div>
        </div>
        {campo('support_reason', t('Perché questo periodo di supporto'))}
      </div>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        {campo('vuln_contact', t('Punto di contatto per le vulnerabilità'), t('Un indirizzo email o una pagina per segnalare (art. 13(17)).'))}
        {campo('cvd_policy_url', t('Politica di divulgazione coordinata (URL)'))}
      </div>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        {campo('owner_email', t('Responsabile (email)'), t('Riceve gli avvisi del registro.'), 'email')}
        {campo('website', t('Sito del fabbricante (https)'), t('Il dominio dell’editore negli avvisi CSAF.'))}
      </div>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        {campo('csirt', t('CSIRT coordinatore'), t('Quello dello Stato membro dello stabilimento principale.'))}
        <div className="field" style={{ flex: 1, minWidth: 240 }}>
          <label htmlFor="cra-stati">{t('Stati membri in cui è disponibile')}</label>
          <input id="cra-stati" type="text" value={stati} onChange={(e) => setStati(e.target.value)} placeholder="IT, FR, DE" />
          <div className="field__hint">{t('Codici di due lettere: la preallerta li indica.')}</div>
        </div>
      </div>
      <fieldset className="field">
        <legend>{t('Progetti che lo costruiscono')}</legend>
        {(progetti.data ?? []).length === 0 ? <p className="field__hint">{t('Nessun progetto in Software prodotto: i rilasci si aggiungono a mano.')}</p>
          : (progetti.data ?? []).map((x) => (
            <div className="checkbox" key={x.id}>
              <input id={`prog-${x.id}`} type="checkbox" checked={d.project_ids.includes(x.id)}
                onChange={(e) => setD({ ...d, project_ids: e.target.checked ? [...d.project_ids, x.id] : d.project_ids.filter((y) => y !== x.id) })} />
              <label htmlFor={`prog-${x.id}`} className="mono">{x.path}</label>
            </div>
          ))}
      </fieldset>
      <div className="field" style={{ maxWidth: 420 }}>
        <label htmlFor="cra-regola">{t('Rilasci dai tag')}</label>
        <select id="cra-regola" value={d.release_rule} onChange={(e) => setD({ ...d, release_rule: e.target.value as 'tag' | 'manual' })}>
          <option value="tag">{t('Ogni tag con SBOM diventa un rilascio')}</option>
          <option value="manual">{t('I tag sono proposti, li conferma una persona')}</option>
        </select>
      </div>
      <div className="toolbar">
        <button type="button" onClick={() => void salva()} disabled={lavoro || !d.name.trim()}>{lavoro ? t('Salvataggio…') : t('Salva')}</button>
      </div>
    </Finestra>
  )
}

export function FinestraRilascio({ prodotto, onChiudi, onFatto }: {
  prodotto: { id: string; name: string }
  onChiudi: () => void
  onFatto: (r: CraRelease) => void
}) {
  const [versione, setVersione] = useState('')
  const [uscito, setUscito] = useState('')
  const [fino, setFino] = useState('')
  const [note, setNote] = useState('')
  const [sbom, setSbom] = useState('')
  const [nomeFile, setNomeFile] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function salva() {
    setErrore(null)
    setLavoro(true)
    try {
      onFatto(await api.craReleaseAdd(prodotto.id, { version: versione, released_at: uscito || null,
        supported_until: fino || null, notes: note, sbom }))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={t('Aggiungi un rilascio')} sotto={prodotto.name} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <p className="field__hint" style={{ marginTop: 0 }}>{t('Anche un rilascio già sul mercato prima del CRA: gli obblighi di segnalazione valgono anche per quelli (art. 69(3)).')}</p>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor="ril-versione">{t('Versione')}</label>
          <input id="ril-versione" type="text" value={versione} onChange={(e) => setVersione(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 160 }}>
          <label htmlFor="ril-uscito">{t('Uscito il')}</label>
          <input id="ril-uscito" type="date" value={uscito} onChange={(e) => setUscito(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 160 }}>
          <label htmlFor="ril-fino">{t('Supportato fino al')}</label>
          <input id="ril-fino" type="date" value={fino} onChange={(e) => setFino(e.target.value)} />
          <div className="field__hint">{t('Vuoto: il periodo del prodotto.')}</div>
        </div>
      </div>
      <div className="field">
        <label htmlFor="ril-sbom">{t('SBOM del rilascio (CycloneDX o SPDX, JSON)')}</label>
        <input id="ril-sbom" type="file" accept=".json,application/json"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (!f) return
            setNomeFile(f.name)
            void f.text().then(setSbom)
          }} />
        <div className="field__hint">{nomeFile ? t('{f}: si conserva com’è, per dieci anni o per il periodo di supporto.', { f: nomeFile }) : t('Facoltativo, ma senza SBOM CENSO non vede le vulnerabilità di questo rilascio.')}</div>
      </div>
      <div className="field">
        <label htmlFor="ril-note">{t('Note')}</label>
        <textarea id="ril-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="toolbar">
        <button type="button" onClick={() => void salva()} disabled={lavoro || !versione.trim()}>{lavoro ? t('Salvataggio…') : t('Aggiungi')}</button>
      </div>
    </Finestra>
  )
}

export function FinestraNuovaSegnalazione({ prodotti, prodotto, onChiudi, onFatto }: {
  prodotti: Array<{ id: string; name: string }>
  prodotto?: string
  onChiudi: () => void
  onFatto: (id: string) => void
}) {
  const [pid, setPid] = useState(prodotto ?? prodotti[0]?.id ?? '')
  const [tipo, setTipo] = useState<'vulnerability' | 'incident'>('vulnerability')
  const [titolo, setTitolo] = useState('')
  const [vuln, setVuln] = useState('')
  const [descrizione, setDescrizione] = useState('')
  const [consapevole, setConsapevole] = useState(false)
  const [quando, setQuando] = useState(locale(null))
  const [malevolo, setMalevolo] = useState<boolean | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function apri() {
    setErrore(null)
    setLavoro(true)
    try {
      const r = await api.craReportOpen({ product_id: pid, kind: tipo, title: titolo, vuln_id: vuln, description: descrizione,
        aware_at: consapevole ? daLocale(quando) : null, malicious: tipo === 'incident' ? malevolo : null })
      onFatto(r.id)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={t('Nuova segnalazione CRA')} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ minWidth: 240, flex: 1 }}>
          <label htmlFor="seg-prodotto">{t('Prodotto')}</label>
          <select id="seg-prodotto" value={pid} onChange={(e) => setPid(e.target.value)}>
            {prodotti.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 260 }}>
          <label htmlFor="seg-tipo">{t('Tipo')}</label>
          <select id="seg-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as 'vulnerability' | 'incident')}>
            <option value="vulnerability">{t('Vulnerabilità sfruttata attivamente (art. 14(2))')}</option>
            <option value="incident">{t('Incidente grave sulla sicurezza del prodotto (art. 14(4))')}</option>
          </select>
        </div>
      </div>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ flex: 1, minWidth: 260 }}>
          <label htmlFor="seg-titolo">{t('Titolo')}</label>
          <input id="seg-titolo" type="text" value={titolo} onChange={(e) => setTitolo(e.target.value)} />
        </div>
        {tipo === 'vulnerability' && (
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="seg-vuln">{t('Identificativo (CVE…)')}</label>
            <input id="seg-vuln" type="text" className="mono" value={vuln} onChange={(e) => setVuln(e.target.value)} />
          </div>
        )}
      </div>
      <div className="field">
        <label htmlFor="seg-descrizione">{t('Descrizione')}</label>
        <textarea id="seg-descrizione" rows={3} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} />
      </div>
      {tipo === 'incident' && (
        <div className="field" style={{ maxWidth: 420 }}>
          <label htmlFor="seg-malevolo">{t('Sospetto di atti illeciti o malevoli')}</label>
          <select id="seg-malevolo" value={malevolo === null ? '' : String(malevolo)} onChange={(e) => setMalevolo(e.target.value === '' ? null : e.target.value === 'true')}>
            <option value="">{t('da valutare')}</option>
            <option value="true">{t('sì')}</option>
            <option value="false">{t('no')}</option>
          </select>
        </div>
      )}
      <div className="checkbox">
        <input id="seg-consapevole" type="checkbox" checked={consapevole} onChange={(e) => setConsapevole(e.target.checked)} />
        <label htmlFor="seg-consapevole">{t('Confermo adesso quando l’organizzazione ne è venuta a conoscenza')}<small>{t('Da quel momento partono le 24 e le 72 ore. Si può confermare anche dopo, dalla segnalazione.')}</small></label>
      </div>
      {consapevole && (
        <div className="field" style={{ maxWidth: 260 }}>
          <label htmlFor="seg-quando">{t('Presa di conoscenza')}</label>
          <input id="seg-quando" type="datetime-local" value={quando} onChange={(e) => setQuando(e.target.value)} />
        </div>
      )}
      <div className="toolbar">
        <button type="button" onClick={() => void apri()} disabled={lavoro || !pid || !titolo.trim()}>{lavoro ? t('In corso…') : t('Apri la segnalazione')}</button>
      </div>
    </Finestra>
  )
}


/**
 * «Importa da Business App»: one CRA product per chosen app, linked to it (its
 * criticality follows the app's). Born out of scope «da qualificare»: a
 * Business App is most often software used internally, outside the CRA, and a
 * person says whether it is a product on the EU market.
 */
export function FinestraImportaBusinessApp({ collegate, onChiudi, onFatto }: {
  collegate: Set<string>
  onChiudi: () => void
  onFatto: () => void
}) {
  const app = useAsync(() => api.businessApps(), [])
  const [scelte, setScelte] = useState<string[]>([])
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const libere = (app.data ?? []).filter((a) => !collegate.has(a.id))

  async function importa() {
    setErrore(null)
    setLavoro(true)
    try {
      await api.craImportBusinessApps(scelte)
      onFatto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={t('Importa da Business App')}
              sotto={t('Un prodotto per ogni applicazione scelta, collegato: la criticità segue quella della Business App.')}
              onChiudi={onChiudi}>
      <ErrorBanner error={errore ?? app.error} />
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I prodotti nascono fuori dal CRA, «da qualificare»: una Business App è spesso software usato solo internamente, che il CRA non riguarda. Aprili e metti nel campo quelli che l’organizzazione mette sul mercato UE.')}
      </p>
      {app.loading && !app.data ? <p className="field__hint">{t('Caricamento…')}</p> : libere.length === 0 ? (
        <p className="field__hint">{t('Ogni Business App è già collegata a un prodotto, o non ce ne sono.')}</p>
      ) : (
        <div className="estensione-ci__elenco">
          {libere.map((a) => (
            <label key={a.id} className="eagle-interruttore">
              <input type="checkbox" checked={scelte.includes(a.id)}
                     onChange={(e) => setScelte(e.target.checked ? [...scelte, a.id] : scelte.filter((x) => x !== a.id))} />{' '}
              {a.name} <CriticitaBadge value={a.criticality} />
            </label>
          ))}
        </div>
      )}
      <div className="toolbar" style={{ marginTop: 12 }}>
        <button type="button" disabled={lavoro || scelte.length === 0} onClick={() => void importa()}>
          {lavoro ? t('Importazione…') : t('Importa {n} applicazioni', { n: scelte.length })}
        </button>
        <button type="button" className="secondary" onClick={onChiudi}>{t('Annulla')}</button>
      </div>
    </Finestra>
  )
}
